import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload, 
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
  Clock, CheckCircle, Save, Loader2, LogIn, Home,
} from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/ModulesContext';
import { Quiz } from '../components/course/Quiz';
import { Checklist } from '../components/course/Checklist';
import { FillInFields } from '../components/course/FillInFields';
import { PassphraseBuilder } from '../components/course/PassphraseBuilder';
import { TipBox } from '../components/course/TipBox';
import { ModuleNavigation } from '../components/course/ModuleNavigation';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const iconMap = {
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload,
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ModulePage() {
  const { slug } = useParams();
  const { modules: courseModules, loaded: modulesLoaded } = useModules();
  const { setCurrentModule, isModuleComplete } = useProgress();
  const { isAuthenticated, token } = useAuth();

  const module = courseModules.find((m) => m.slug === slug);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (module) setCurrentModule(module.id);
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLocked(false);
      setNotFound(false);
      setFetchError(null);
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(`${BACKEND}/api/modules/${slug}`, { headers });
        if (resp.status === 401 || resp.status === 403) {
          if (!cancelled) setLocked(true);
        } else if (resp.status === 404) {
          if (!cancelled) setNotFound(true);
        } else if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) setContent(data.module);
        } else {
          if (!cancelled) setFetchError('Could not load this module. Please refresh and try again.');
        }
      } catch (e) {
        if (!cancelled) setFetchError('Could not load this module. Please check your connection.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, module?.id]);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto py-10 text-center fade-in" data-testid="module-not-found">
        <AlertTriangle className="w-14 h-14 text-stone-400 mx-auto mb-4" />
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-3">Module not found</h1>
        <p className="text-text-muted mb-6">We couldn't find a module at this address. It may have moved.</p>
        <Link to="/"><Button className="rounded-full px-6 py-4 font-semibold"><Home className="w-4 h-4 mr-2" />Back to home</Button></Link>
      </div>
    );
  }

  if (!modulesLoaded && !module && loading) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
        <p className="text-text-muted">Loading module…</p>
      </div>
    );
  }

  const Icon = (module && iconMap[module.icon]) || BookOpen;
  const isComplete = module && isModuleComplete(module.id);
  const isFreeModule = !!module?.isFree;

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto fade-in" data-testid="module-locked-page">
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-8 md:p-12 text-center shadow-sm">
          <div className="w-20 h-20 bg-primary/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Lock className="w-10 h-10 text-primary" />
          </div>
          <div className="inline-flex items-center gap-2 text-sm text-text-muted mb-3 justify-center flex-wrap">
            {module && (
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                Module {module.id} of {courseModules.length}
              </span>
            )}
            {module?.estimatedTime && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {module.estimatedTime}
              </span>
            )}
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2" data-testid="locked-module-title">
            {module?.title || 'This module'}
          </h1>
          {module?.description && (
            <p className="text-text-muted text-lg mb-6" data-testid="locked-module-description">{module.description}</p>
          )}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 mb-6 text-left max-w-md mx-auto">
            <p className="font-semibold text-text mb-2">This module is part of the full course.</p>
            <ul className="space-y-1.5 text-sm text-text-muted">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success flex-shrink-0" />All 12 modules</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success flex-shrink-0" />Interactive quizzes & checklists</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success flex-shrink-0" />Completion certificate</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success flex-shrink-0" />Lifetime access</li>
            </ul>
          </div>
          <div className="flex flex-col gap-3 max-w-sm mx-auto">
            <Link to="/purchase">
              <Button data-testid="locked-unlock-btn" className="w-full rounded-full py-6 text-lg font-bold bg-primary text-white hover:bg-primary-600">
                Unlock the full course
              </Button>
            </Link>
            {!isAuthenticated && (
              <Link to="/login" state={{ from: `/module/${slug}` }}>
                <Button data-testid="locked-signin-btn" variant="outline" className="w-full rounded-full py-5 font-semibold">
                  <LogIn className="w-4 h-4 mr-2" />
                  Already paid? Sign in
                </Button>
              </Link>
            )}
          </div>
          <p className="text-text-muted text-sm mt-6">
            <Link to="/module/introduction" className="text-primary font-semibold hover:underline" data-testid="locked-try-free">Module 1 is free to try</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto fade-in">
      <header className="mb-8">
        <div className="flex items-center gap-3 text-sm text-text-muted mb-4 flex-wrap">
          <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
            Module {module?.id} of {courseModules.length}
          </span>
          {module?.estimatedTime && (
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {module.estimatedTime}
            </span>
          )}
          {isComplete && (
            <span className="flex items-center gap-1 text-success">
              <CheckCircle className="w-4 h-4" />
              Completed
            </span>
          )}
          {isFreeModule && (
            <span className="flex items-center gap-1 bg-secondary/20 text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium">
              Free Preview
            </span>
          )}
        </div>

        <div className="flex items-start gap-4">
          <div className={cn(
            "w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0",
            isComplete ? "bg-success/10 text-success" : "bg-primary/10 text-primary"
          )}>
            <Icon className="w-8 h-8" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">
              {module?.title || content?.title}
            </h1>
            <p className="text-text-muted text-lg">
              {module?.description || content?.description}
            </p>
          </div>
        </div>
      </header>

      {loading && (
        <div className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-text-muted">Loading module…</p>
        </div>
      )}

      {fetchError && !loading && (
        <div className="bg-error/10 border border-error/30 rounded-xl p-5 text-error">{fetchError}</div>
      )}

      {!loading && content && (
        <article className="space-y-8">
          {isFreeModule && !isAuthenticated && (
            <section
              data-testid="create-account-prompt"
              className="bg-gradient-to-r from-secondary/15 to-secondary/5 border-2 border-secondary/40 rounded-2xl p-5 md:p-6"
            >
              <div className="flex items-start gap-3 flex-col sm:flex-row sm:items-center">
                <div className="w-10 h-10 bg-secondary/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Save className="w-5 h-5 text-secondary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-text">Enjoying this? Save your progress.</p>
                  <p className="text-text-muted text-sm">Create a free account so your notes, checklists and modules are there every time you log in — on any device.</p>
                </div>
                <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                  <Link to="/signup" state={{ from: `/module/${module?.slug}` }} className="w-full sm:w-auto">
                    <Button data-testid="module-prompt-signup" className="w-full rounded-xl bg-primary text-white">Create free account</Button>
                  </Link>
                  <Link to="/login" state={{ from: `/module/${module?.slug}` }} className="w-full sm:w-auto">
                    <Button data-testid="module-prompt-signin" variant="outline" className="w-full rounded-xl">Sign in</Button>
                  </Link>
                </div>
              </div>
            </section>
          )}

          <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
            <p className="text-text text-lg leading-relaxed">{content.content?.intro}</p>
          </section>

          {(content.content?.sections || []).map((section, index) => (
            <section
              key={index}
              className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 slide-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <h2 className="font-heading font-bold text-xl md:text-2xl text-text mb-4">{section.title}</h2>
              <ul className="space-y-3">
                {(section.content || []).map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-3 text-text-muted">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {content.content?.warning && <TipBox variant="warning" title="Important">{content.content.warning}</TipBox>}
          {content.content?.tip && <TipBox variant="tip" mascot={content.content.tip.mascot}>{content.content.tip.text}</TipBox>}
          {content.content?.exercise?.type === 'passphrase-builder' && <PassphraseBuilder />}
          {content.content?.exercise?.type === 'website-checker' && (
            <div className="bg-gradient-to-br from-info/10 to-info/5 rounded-2xl border-2 border-info/20 p-6 md:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-info" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-text mb-1">{content.content.exercise.title}</h3>
                  <p className="text-text-muted text-sm">{content.content.exercise.instructions}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-info/20">
                <p className="text-text-muted text-sm mb-3"><strong>Try it now:</strong> Open a new tab and go to a website you use often (like your bank or email). Look for:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success" /><span>A padlock icon in the address bar</span></li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-success" /><span>The address starts with <code className="bg-stone-100 px-2 py-0.5 rounded">https://</code></span></li>
                </ul>
              </div>
            </div>
          )}

          {content.checklist && module && <Checklist moduleId={module.id} items={content.checklist} />}
          {content.fillInFields && module && <FillInFields moduleId={module.id} fields={content.fillInFields} />}
          {content.quiz && module && (
            <Quiz
              moduleId={module.id}
              question={content.quiz.question}
              options={content.quiz.options}
              requiresAuth={!isAuthenticated}
            />
          )}

          {module && <ModuleNavigation currentModule={module} />}
        </article>
      )}
    </div>
  );
}
